/**
 * Expands promo codes for the preview seller by repeating each unique code once per ticket quantity.
 * For all other sellers the codes are returned unchanged.
 *
 * Formerly named `augmentPromoCodes`.
 *
 * Given x total ticket quantity across all line items, repeats each unique promo code x times.
 * This mirrors legacy behaviour where each physical ticket required its own redemption code.
 *
 * **Legacy (`react-ticketing-library`):** received `LineItem[]` from the Apollo GraphQL cache.
 * **New (`nextjs-ticketure-ticketing-webapp`):** receives `Transformed.LineItem[]` from the
 * TanStack Query cart cache — same shape, same logic.
 *
 * @example
 * // returns ['one', 'one', 'one']
 * expandPromoCodesForPreview({ promoCodes: ['one'], isPreview: true, lineItems: [{ quantity: 3 }] })
 *
 * @param {object} param
 * @param {string[]} param.promoCodes - Incoming promo codes from the URL
 * @param {boolean} param.isPreview - Whether the seller is the preview seller
 * @param {ReadonlyArray<Transformed.LineItem>} param.lineItems - Line items currently in the cart
 * @returns {string[]} The (potentially expanded) promo codes array
 */
export const expandPromoCodesForPreview = ({
  promoCodes,
  isPreview,
  lineItems,
}: {
  promoCodes: string[]
  isPreview: boolean
  lineItems: readonly Transformed.LineItem[]
}): string[] => {
  let nextCodes = promoCodes

  // Only expand for preview — other sellers apply one code per order
  if (isPreview) {
    const lineItemsCount = lineItems.reduce((acc, lineItem) => acc + lineItem.quantity, 0)
    const uniques = [...new Set(nextCodes)]
    nextCodes = uniques.reduce((acc, code) => {
      for (let i = 0; i < lineItemsCount; i += 1) {
        acc.push(code)
      }
      return acc
    }, [] as string[])
  }

  return nextCodes
}
